/**
 * Versionskennung der laufenden App und Kennzeichnung der Anfragen an den Dienst (ADR-0041 §1, §2).
 *
 * Die Kennung steht in der App-Shell (`<meta name="dj-version">`, gesetzt im Image-Bau, werkzeuge/version.mjs); sie ist
 * die Version, die gerade läuft, auch wenn ein neuer Service Worker schon wartet. Jede Anfrage an `/api/` trägt sie im
 * Kopf `X-App-Version`, dazu eine zufällige Kennung des Geräts und dessen Bezeichnung (für die Versionsübersicht
 * `make app-versionen`). Nennt eine Antwort eine andere Version, erfährt es der Lebenszyklus (plattform/lebenszyklus.ts).
 */
import { geraetebezeichnungVorschlag } from "./geraet";

const PLATZHALTER = "__DJ_" + "VERSION__";
export const VERSION_ENTWICKLUNG = "entwicklung";

function kennungAusShell(): string {
  const wert = globalThis.document?.querySelector('meta[name="dj-version"]')?.getAttribute("content")?.trim();
  return wert && wert !== PLATZHALTER ? wert : VERSION_ENTWICKLUNG;
}

/** Version der laufenden App */
export const VERSION = kennungAusShell();

const GERAET_SCHLUESSEL = "dj-geraet";

/**
 * Zufällige Kennung dieses Geräts (Browser-Profil), einmal erzeugt und im `localStorage` gemerkt. Nicht über
 * `crypto.randomUUID`: das vergibt die Meldungs-Ids (teststeuerung.md §10).
 */
export function geraetekennung(): string {
  try {
    const gemerkt = localStorage.getItem(GERAET_SCHLUESSEL);
    if (gemerkt && /^[a-f0-9]{32}$/.test(gemerkt)) return gemerkt;
    const neu = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
    localStorage.setItem(GERAET_SCHLUESSEL, neu);
    return neu;
  } catch {
    return "";
  }
}

/** Köpfe, mit denen die App sich bei jeder Anfrage an den Dienst ausweist (nur Angabe, nie Berechtigung). */
export function versionskoepfe(): Record<string, string> {
  const kopf: Record<string, string> = { "X-App-Version": VERSION };
  const geraet = geraetekennung();
  if (geraet) {
    kopf["X-App-Geraet"] = geraet;
    kopf["X-App-Geraetebezeichnung"] = encodeURIComponent(geraetebezeichnungVorschlag());
  }
  return kopf;
}

type Beobachter = (version: string) => void;
const beobachter = new Set<Beobachter>();

/** Meldet jede Version des Dienstes, die von der eigenen abweicht (Antwortkopf `X-App-Version`). */
export function abweichendeVersionBeobachten(f: Beobachter): () => void {
  beobachter.add(f);
  return () => beobachter.delete(f);
}

export function antwortPruefen(r: Response): void {
  const v = r.headers.get("X-App-Version");
  if (v && v !== VERSION) for (const b of beobachter) b(v);
}

/** `fetch` an den eigenen Dienst: mit den Versionsköpfen, und die Version der Antwort geht an den Lebenszyklus. */
export async function apiFetch(eingabe: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  for (const [k, w] of Object.entries(versionskoepfe())) if (!headers.has(k)) headers.set(k, w);
  const r = await fetch(eingabe, { ...init, headers });
  antwortPruefen(r);
  return r;
}
